import { useFetch } from '@vueuse/core';
import { computed, ref } from 'vue';
import type {
    FormattedComponents,
    GoogleAddressComponent,
    GooglePlace,
    GooglePlaceDetailsResponse,
    GooglePlacesSearchResponse,
} from './address-autocomplete.types';

const GOOGLE_API_KEY = import.meta.env.VITE_GOOGLE_API_KEY;
const MAX_RESULTS = 10;

type UseFetchParams = Exclude<Parameters<typeof useFetch>[2], undefined>;

/**
 * Formats Google address components into individual components
 */
export function formatAddressComponents(addressComponents: GoogleAddressComponent[]): FormattedComponents {
    const components = {
        streetNumber: '',
        streetName: '',
        city: '',
        state: '',
        zipCode: '',
        zipExtension: '',
        country: '',
    };

    addressComponents.forEach(component => {
        const types = component.types;
        if (types) {
            if (types.includes('street_number')) {
                components.streetNumber = component.longText;
            } else if (types.includes('route')) {
                components.streetName = component.longText;
            } else if (types.includes('locality')) {
                components.city = component.longText;
            } else if (types.includes('administrative_area_level_1')) {
                components.state = component.shortText;
            } else if (types.includes('postal_code')) {
                const zipParts = component.longText.split('-');
                components.zipCode = zipParts[0];
                if (zipParts[1]) {
                    components.zipExtension = zipParts[1];
                }
            } else if (types.includes('country')) {
                components.country = component.longText;
            }
        }
    });

    return {
        ...components,
    };
}

/**
 * Service for searching places using Google Places API
 */
export const useSearchPlaces = () => {
    const searchQuery = ref<string>('');
    const maxResults = ref<number>(MAX_RESULTS);

    const options: UseFetchParams = {
        immediate: false,
        beforeFetch: ({ cancel, options }) => {
            if (!searchQuery.value.trim()) {
                console.warn('[useSearchPlaces] Fetch cancelled - Empty search query');
                cancel();
            }

            options.headers = {
                'Content-Type': 'application/json',
                'X-Goog-Api-Key': GOOGLE_API_KEY,
                'X-Goog-FieldMask': 'places.displayName,places.formattedAddress,places.id,places.addressComponents',
                ...options.headers,
            };

            options.body = JSON.stringify({
                textQuery: searchQuery.value,
                maxResultCount: maxResults.value,
                languageCode: 'en',
            });
        },
    };

    const useFetchReturn = useFetch('https://places.googleapis.com/v1/places:searchText', options)
        .post()
        .json<GooglePlacesSearchResponse>();

    const processedData = computed(() => {
        if (!useFetchReturn.data.value?.places || useFetchReturn.data.value.places.length === 0) {
            return [];
        }

        return useFetchReturn.data.value.places.map((place: GooglePlace) => {
            const formatted = formatAddressComponents(place.addressComponents || []);
            return {
                searchResult: {
                    place_id: place.id,
                    description: place.formattedAddress || place.displayName?.text,
                },
                label: place.formattedAddress || place.displayName?.text,
                addressComponents: place.addressComponents || [],
                formattedAddress: place.formattedAddress || place.displayName?.text || '',
                formattedComponents: formatted,
            };
        });
    });

    return {
        data: processedData,
        isFetching: useFetchReturn.isFetching,
        error: useFetchReturn.error,
        statusCode: useFetchReturn.statusCode,
        response: useFetchReturn.response,
        searchPlaces: (query: string, results = 10) => {
            searchQuery.value = query;
            maxResults.value = results;
            return useFetchReturn.execute();
        },
    };
};

/**
 * Service for fetching place details using Google Places API
 */
export const usePlaceDetails = () => {
    const placeId = ref<string>('');

    const options: UseFetchParams = {
        immediate: false,
        beforeFetch: ({ cancel, options }) => {
            if (!placeId.value) {
                console.warn('[usePlaceDetails] Fetch cancelled - No place ID set');
                cancel();
            }

            options.headers = {
                'X-Goog-Api-Key': GOOGLE_API_KEY,
                'X-Goog-FieldMask': 'id,displayName,formattedAddress,addressComponents,location',
                ...options.headers,
            };
        },
    };

    const useFetchReturn = useFetch(
        computed(() => `https://places.googleapis.com/v1/places/${placeId.value}`),
        options,
    ).json<GooglePlaceDetailsResponse>();

    return {
        data: useFetchReturn.data,
        isFetching: useFetchReturn.isFetching,
        error: useFetchReturn.error,
        statusCode: useFetchReturn.statusCode,
        response: useFetchReturn.response,
        getPlaceDetails: (id: string) => {
            placeId.value = id;
            return useFetchReturn.execute();
        },
    };
};
